import { Dispatch, SetStateAction, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import LogoBlack from "@/assets/logo/logo-black.svg";
import LogoWhite from "@/assets/logo/logo-white.svg";
import { useTheme } from "next-themes";
import { siteConfig } from "@/config/site";
import { Modal } from "@/components/ui/modal";
import { UserAuthForm } from "@/components/forms/user-auth-form";


function SignInModal({
  showSignInModal,
  setShowSignInModal,
}: {
  showSignInModal: boolean;
  setShowSignInModal: Dispatch<SetStateAction<boolean>>;
}) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <Modal showModal={showSignInModal} setShowModal={setShowSignInModal}>
      <div className="w-full">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 px-4 py-8 sm:w-[350px] sm:px-0">
          <div className="flex flex-col space-y-2 text-center">
            <a href={siteConfig.url} className="mx-auto">
              {mounted ? (
                <Image
                  src={resolvedTheme === "dark" ? LogoWhite : LogoBlack}
                  alt="Watchero Logo"
                  className="h-6 w-auto"
                />
              ) : (
                <Image
                  src={LogoBlack}
                  alt="Watchero Logo"
                  className="h-6 w-auto"
                />
              )}
            </a>
            <h1 className="text-2xl font-semibold tracking-tight">
              Welcome back
            </h1>
            <p className="text-sm text-muted-foreground">
              Enter your email to sign in to your account
            </p>
          </div>
          <Suspense>
            <UserAuthForm />
          </Suspense>
          <p className="px-8 text-center text-sm text-muted-foreground">
            <Link
              href="/register"
              className="hover:text-brand underline underline-offset-4"
              onClick={() => setShowSignInModal(false)}
            >
              Don&apos;t have an account? Sign Up
            </Link>
          </p>
        </div>
      </div>
    </Modal>
  );
}

export function useSignInModal() {
  const [showSignInModal, setShowSignInModal] = useState(false);

  const SignInModalCallback = useCallback(() => {
    return (
      <SignInModal
        showSignInModal={showSignInModal}
        setShowSignInModal={setShowSignInModal}
      />
    );
  }, [showSignInModal, setShowSignInModal]);

  return useMemo(
    () => ({
      setShowSignInModal,
      SignInModal: SignInModalCallback,
    }),
    [setShowSignInModal, SignInModalCallback],
  );
}